import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AdminSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { initializeApp, getApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';


function Employees() {
  const [activeTab, setActiveTab] = useState('driver'); // 'driver' or 'technician'
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();

  // Navigation State Handler
  const location = useLocation();
  useEffect(() => {
    if (location.state?.openCreateModal) {
      setIsCreating(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    email: '',
    phone: '',
    employeeId: '',
    department: '',
    location: '',
    password: '',
    countryCode: 'AE',
    callingCode: '971',
    role: 'driver' // default
  });

  // Fetch Employees
  useEffect(() => {
    const q = query(collection(db, 'employees'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const employeesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesData);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching employees:", error);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      // Use a secondary app to avoid logging out the admin
      let secondaryApp;
      if (getApps().length > 1) {
        secondaryApp = getApps().find(app => app.name === 'Secondary');
        if (!secondaryApp) secondaryApp = initializeApp(getApp().options, 'Secondary');
      } else {
        secondaryApp = initializeApp(getApp().options, 'Secondary');
      }

      const secondaryAuth = getAuth(secondaryApp);

      const { email, password, name, phone, employeeId, department, location, role, countryCode, callingCode } = newEmployee;

      // Create User in Auth
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const user = userCredential.user;

      // Save to Firestore (using main app db)
      const employeeData = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: `+${callingCode}${phone.trim()}`,
        employeeId: employeeId.trim(),
        department: department.trim(),
        location: location.trim(),
        role: role,
        status: 'active',
        isActive: true,
        availability: 'available',
        userId: user.uid,
        countryCode: countryCode,
        createdAt: serverTimestamp(),
        createdBy: 'admin',
        // profileImageUrl: null // Add if upload implemented
      };

      await setDoc(doc(db, 'employees', user.uid), employeeData);

      // Create Notification
      await addDoc(collection(db, 'admin_notifications'), {
        type: 'employee_created',
        title: `New ${role} Created`,
        message: `${role} ${name} has been created successfully.`,
        employeeId: user.uid,
        employeeData: employeeData,
        read: false,
        createdAt: serverTimestamp(),
      });

      // Cleanup secondary auth
      await signOut(secondaryAuth);

      alert(`${role} created successfully!`);
      setIsModalOpen(false);
      setNewEmployee({
        name: '', email: '', phone: '', employeeId: '',
        department: '', location: '', password: '',
        countryCode: 'AE', callingCode: '971', role: activeTab
      });

    } catch (error) {
      console.error("Error creating employee:", error);
      alert("Failed to create employee: " + error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const toggleStatus = async (employee) => {
    const newStatus = employee.status === 'active' ? 'inactive' : 'active';
    try {
      await updateDoc(doc(db, 'employees', employee.id), { status: newStatus, isActive: newStatus === 'active' });
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this employee? This cannot be undone.")) {
      try {
        await deleteDoc(doc(db, 'employees', id));
      } catch (err) {
        console.error("Error deleting employee:", err);
        alert("Failed to delete employee");
      }
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesTab = emp.role === activeTab;
    const matchesSearch = (emp.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (emp.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (emp.employeeId?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const getStatusColor = (status) => status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <AdminSidebar />
      <div
        className="flex-1 overflow-y-auto transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
      >
        <div className="p-8">
          {isMobile && (
            <button
              onClick={toggleMobileSidebar}
              className="mb-4 text-gray-600 focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <div className="flex items-center">
                {isMobile && (
                  <button
                    onClick={toggleMobileSidebar}
                    className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                )}
                <h1 className="text-3xl font-bold text-gray-800">Employees</h1>
              </div>
              <p className="text-gray-500 mt-1">Manage drivers and technicians</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setActiveTab('driver'); setNewEmployee(prev => ({ ...prev, role: 'driver' })); setIsModalOpen(true); }}
                className="bg-brand-golden hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Driver
              </button>
              <button
                onClick={() => { setActiveTab('technician'); setNewEmployee(prev => ({ ...prev, role: 'technician' })); setIsModalOpen(true); }}
                className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Technician
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Tabs */}
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('driver')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'driver' ? 'bg-white shadow text-black' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Drivers
              </button>
              <button
                onClick={() => setActiveTab('technician')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'technician' ? 'bg-white shadow text-black' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Technicians
              </button>
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder={`Search ${activeTab}s...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-golden"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">ID / Dept</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEmployees.map((employee) => (
                  <tr
                    key={employee.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedEmployee(employee)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-brand-golden/20 flex items-center justify-center text-brand-dark font-bold">
                          {employee.name?.charAt(0)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{employee.name}</div>
                          <div className="text-xs text-gray-500">{employee.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{employee.employeeId}</div>
                      <div className="text-xs text-gray-500">{employee.department}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">{employee.email}</div>
                      <div className="text-xs text-gray-500">{employee.phone}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(employee.status)}`}>
                        {employee.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleStatus(employee); }}
                          className={`p-1 rounded hover:bg-gray-100 ${employee.status === 'active' ? 'text-red-600' : 'text-green-600'}`}
                          title={employee.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={employee.status === 'active' ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(employee.id); }}
                          className="p-1 rounded hover:bg-gray-100 text-red-500"
                          title="Delete"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredEmployees.length === 0 && !isLoading && (
              <div className="p-8 text-center text-gray-500">No employees found.</div>
            )}
          </div>
        </div>
      </div>

      {/* Create Employee Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Add New {newEmployee.role === 'driver' ? 'Driver' : 'Technician'}</h2>
            <form onSubmit={handleCreateEmployee} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    required
                    value={newEmployee.name}
                    onChange={e => setNewEmployee({ ...newEmployee, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={newEmployee.email}
                    onChange={e => setNewEmployee({ ...newEmployee, email: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    required
                    value={newEmployee.phone}
                    onChange={e => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    placeholder="501234567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Employee ID</label>
                  <input
                    type="text"
                    required
                    value={newEmployee.employeeId}
                    onChange={e => setNewEmployee({ ...newEmployee, employeeId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Department</label>
                  <input
                    type="text"
                    required
                    value={newEmployee.department}
                    onChange={e => setNewEmployee({ ...newEmployee, department: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <input
                    type="text"
                    required
                    value={newEmployee.location}
                    onChange={e => setNewEmployee({ ...newEmployee, location: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Password (min 6 chars)</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={newEmployee.password}
                    onChange={e => setNewEmployee({ ...newEmployee, password: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="px-4 py-2 text-black bg-brand-golden rounded-md hover:bg-yellow-600 disabled:opacity-50"
                >
                  {isCreating ? 'Creating...' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Employee Details Modal */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedEmployee(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="relative bg-brand-golden h-32">
              <button
                onClick={() => setSelectedEmployee(null)}
                className="absolute top-4 right-4 text-white hover:bg-black/20 rounded-full p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-8 pb-8">
              <div className="relative -mt-16 mb-6">
                <div className="w-32 h-32 rounded-full border-4 border-white bg-white shadow-md overflow-hidden flex items-center justify-center">
                  {selectedEmployee.profileImageUrl ? (
                    <img
                      src={selectedEmployee.profileImageUrl}
                      alt={selectedEmployee.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-4xl font-bold text-gray-400">
                      {selectedEmployee.name?.charAt(0)}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedEmployee.name}</h2>
                <p className="text-brand-golden font-medium">{selectedEmployee.role} • {selectedEmployee.department}</p>
                <div className="flex gap-2 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedEmployee.status)}`}>
                    {selectedEmployee.status?.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contact Information</label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center text-gray-600">
                        <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {selectedEmployee.email}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {selectedEmployee.phone}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Employment Details</label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center text-gray-600">
                        <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0h4" />
                        </svg>
                        ID: {selectedEmployee.employeeId}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {selectedEmployee.location}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Employees;